import type {
  KambiSportsSameGameMultiSelection,
  KambiSportsSelection,
  KambiSportsSingleSelection,
} from '@mobi/betslip/types'
import type { KambiPlaceBetRequest } from '@core/Areas/KambiSports/helpers/api/betting/types'
import {
  postPlaceKambiSportsBet,
  postValidateKambiSportsBet,
} from '@core/Areas/KambiSports/helpers/api/betting'
import {
  ConfirmBet,
  ConfirmKambiBetFailed,
  ConfirmKambiBetSucceeded,
  ProposeBet,
  ProposeKambiBetFailed,
  ProposeKambiBetSucceeded,
} from '@core/Areas/Quickbet/signals'
import {
  isKambiSportsMultiSelection,
  isKambiSportsSameGameMultiSelection,
  isKambiSportsSingleSelection,
} from '@mobi/betslip/helpers/typeGuards'
import { formatDollarDecimalToThousandMultplier as convertToKambiSystemCurrency } from '@core/Areas/KambiSports/helpers/currencyFormat'

// TODO:
// * Handle unknown bet selection type
// * Handle Propose Errors
// * Handle Commit Errors

export async function proposeKambiBetAsync({
  selection,
}: {
  selection: KambiSportsSelection
}): Promise<void> {
  ProposeBet()
  try {
    const response = await postValidateKambiSportsBet(
      createBetRequest({ selection, investmentWin: 1 })
    )

    if (response.status === 'SUCCESS') {
      ProposeKambiBetSucceeded()
    } else {
      ProposeKambiBetFailed()
    }
  } catch {
    ProposeKambiBetFailed()
  }
}

export async function confirmKambiBetAsync({
  selection,
  investmentWin,
}: {
  selection: KambiSportsSelection
  investmentWin: number
}): Promise<void> {
  ConfirmBet()
  try {
    const response = await postPlaceKambiSportsBet(createBetRequest({ selection, investmentWin }))

    if (response.status === 'SUCCESS') {
      ConfirmKambiBetSucceeded({ coupon: response.coupon })
    } else {
      ConfirmKambiBetFailed()
    }
  } catch {
    ConfirmKambiBetFailed()
  }
}

// =============
// Local Helpers
// =============

function createBetRequest({
  selection,
  investmentWin,
}: CreateBetRequestParams): Pick<KambiPlaceBetRequest, 'bets' | 'couponRows'> {
  if (isKambiSportsSingleSelection(selection)) {
    return {
      bets: [{ couponRowIndexes: [0], stake: convertToKambiSystemCurrency(investmentWin) }],
      couponRows: [buildSingleCouponRow(selection)],
    }
  }

  if (isKambiSportsSameGameMultiSelection(selection)) {
    return {
      bets: [{ couponRowIndexes: [0], stake: convertToKambiSystemCurrency(investmentWin) }],
      couponRows: [buildSameGameMultiCouponRow(selection)],
    }
  }

  if (isKambiSportsMultiSelection(selection)) {
    const { legs } = selection

    const couponRows: CouponRows = legs.map((leg, index) =>
      isKambiSportsSingleSelection(leg)
        ? buildSingleCouponRow(leg, index)
        : buildSameGameMultiCouponRow(leg, index)
    )

    return {
      bets: [
        {
          couponRowIndexes: couponRows.map((_, index) => index),
          stake: convertToKambiSystemCurrency(investmentWin),
        },
      ],
      couponRows,
    }
  }

  return { couponRows: [], bets: [] }
}

function buildSingleCouponRow(selection: KambiSportsSingleSelection, index = 0): CouponRows[0] {
  const { odds, id } = selection.betOffer.outcomes[0]
  return { index, odds, outcomeId: id, type: 'SIMPLE' }
}

function buildSameGameMultiCouponRow(
  selection: KambiSportsSameGameMultiSelection,
  index = 0
): CouponRows[0] {
  const { betOffers, odds } = selection
  return {
    index,
    odds,
    type: 'BET_BUILDER',
    group: {
      operation: 'AND',
      groups: betOffers.map(({ outcomes }) => ({
        operation: 'AND',
        outcomeIds: [outcomes[0].id],
      })),
    },
  }
}

// =====
// Types
// =====

type CreateBetRequestParams = {
  selection: KambiSportsSelection
  investmentWin: number
}

type CouponRows = KambiPlaceBetRequest['couponRows']
